import { useEffect, useState } from 'react';

type ScriptState = 'loading' | 'ready' | 'error';

// https://usehooks.com/useScript/ 参考
function useScript(src: string): ScriptState {
  // Keep track of script status ("loading", "ready", "error")
  const [status, setStatus] = useState<ScriptState>('loading');
  useEffect(() => {
    // Fetch existing script element by src
    // It may have been added by another intance of this hook
    let script = document.querySelector<HTMLScriptElement>(
      `script[src="${src}"]`
    );
    if (script == null) {
      // Create script
      script = document.createElement('script');
      script.src = src;
      script.async = true;
      script.setAttribute('data-status', 'loading');
      // Add script to document body
      document.body.appendChild(script);
      // Store status in attribute on script
      // This can be read by other instances of this hook
      const setAttributeFromEvent: EventListenerOrEventListenerObject = (
        event
      ) => {
        script?.setAttribute(
          'data-status',
          event.type === 'load' ? 'ready' : 'error'
        );
      };
      script.addEventListener('load', setAttributeFromEvent);
      script.addEventListener('error', setAttributeFromEvent);
    } else {
      // Grab existing script status from attribute and set to state.
      setStatus(script.getAttribute('data-status') as ScriptState);
    }
    // Script event handler to update status in state
    // Note: Even if the script already exists we still need to add
    // event handlers to update the state for *this* hook instance.
    const setStateFromEvent: EventListenerOrEventListenerObject = (event) => {
      setStatus(event.type === 'load' ? 'ready' : 'error');
    };

    // Add event listeners
    script.addEventListener('load', setStateFromEvent);
    script.addEventListener('error', setStateFromEvent);
    // Remove event listeners on cleanup
    return () => {
      if (script) {
        script.removeEventListener('load', setStateFromEvent);
        script.removeEventListener('error', setStateFromEvent);
      }
    };
  }, [src]); // Only re-run effect if script src changes
  return status;
}

export default useScript;
